import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckoutSchema, computeTotals, type Address } from '@harbor/shared';
import { ApiError, apiRequest, newIdempotencyKey } from '../lib/api.js';
import { money } from '../lib/format.js';
import { useCart } from '../state/cart.js';
import { useAuth } from '../state/auth.js';
import { Alert, Badge, Button, Card, EmptyState, Field, Select, TextArea, TextInput } from '../components/ui.js';

/**
 * Checkout.
 *
 * ## The idempotency key is generated once per attempt
 *
 * It is created when the customer lands on the review step and held in a ref. It
 * must not be regenerated on retry — that is precisely the case it exists to
 * protect against. If the request times out after the order was created, the
 * retry carries the same key and the server replays the original response rather
 * than charging twice.
 *
 * ## Test cards are first-class
 *
 * The mock payment provider reproduces Stripe's decline semantics, so the
 * decline path is reachable without a payment account. The card selector is not
 * hidden in a comment: being able to demonstrate a declined payment is the point
 * of building the mock properly.
 */

type Step = 'details' | 'payment' | 'review';

const TEST_CARDS = [
  { token: 'tok_visa', label: 'Visa — succeeds' },
  { token: 'tok_mastercard', label: 'Mastercard — succeeds' },
  { token: 'tok_authenticationRequired', label: '3-D Secure — requires action' },
  { token: 'tok_chargeDeclined', label: 'Declined — card_declined' },
  { token: 'tok_insufficientFunds', label: 'Declined — insufficient_funds' },
  { token: 'tok_expiredCard', label: 'Declined — expired_card' },
];

const EMPTY_ADDRESS: Address = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  region: '',
  postalCode: '',
  country: 'US',
  phone: '',
};

export function Checkout() {
  const navigate = useNavigate();
  const { cart, loading, refresh } = useCart();
  const { user } = useAuth();

  const [step, setStep] = useState<Step>('details');
  const [email, setEmail] = useState(user?.email ?? '');
  const [shipping, setShipping] = useState<Address>({
    ...EMPTY_ADDRESS,
    ...(user ? { name: user.name } : {}),
  });
  const [notes, setNotes] = useState('');
  const [paymentToken, setPaymentToken] = useState('tok_visa');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<{ title: string; detail: string; items?: string[] } | null>(null);

  /**
   * One key per checkout attempt, created lazily and reused across retries.
   * A ref rather than state because changing it must not re-render, and because
   * the value must survive re-renders caused by typing.
   */
  const idempotencyKey = useRef<string | null>(null);

  const totals = useMemo(
    () =>
      computeTotals(
        (cart?.items ?? []).map((item) => ({
          unitPriceMinor: item.unitPriceMinor,
          quantity: item.quantity,
        })),
        {
          shippingFlatMinor: cart?.totals.shippingMinor ?? 0,
          // The API already applied the free-shipping rule; passing its shipping
          // figure through keeps the preview identical to the server's number.
          freeShippingThresholdMinor: 0,
          taxRateBps: 0,
        },
      ),
    [cart],
  );

  if (loading && !cart) {
    return <div className="mx-auto max-w-3xl px-4 py-24 text-center text-muted">Loading basket…</div>;
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6">
        <EmptyState
          title="Your basket is empty"
          description="Add something from the catalogue before checking out."
          action={
            <Link
              to="/"
              className="inline-flex h-11 items-center rounded-lg bg-accent px-4 text-sm font-medium text-accent-ink"
            >
              Browse the catalogue
            </Link>
          }
        />
      </div>
    );
  }

  function validateDetails(): boolean {
    const result = CheckoutSchema.safeParse({
      email,
      shipping,
      billing: null,
      paymentMethodToken: paymentToken,
      notes,
    });

    if (result.success) {
      setFieldErrors({});
      return true;
    }

    // Flatten Zod issues into `field -> message` so the form can show the error
    // next to the input that caused it, instead of a single summary banner.
    const errors: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const path = issue.path.join('.');
      if (!errors[path]) errors[path] = issue.message;
    }
    setFieldErrors(errors);
    return false;
  }

  async function submit() {
    if (!validateDetails()) {
      setStep('details');
      return;
    }

    idempotencyKey.current ??= newIdempotencyKey();
    setSubmitting(true);
    setFailure(null);

    try {
      const response = await apiRequest<{ order: { id: string; number: string } }>('/api/checkout', {
        method: 'POST',
        idempotencyKey: idempotencyKey.current,
        body: {
          email,
          shipping,
          billing: null,
          paymentMethodToken: paymentToken,
          notes,
        },
      });

      // The basket was converted server-side; clear the stale client copy.
      await refresh();
      navigate(`/checkout/confirmation/${response.order.id}`, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFailure({
          title:
            caught.status === 409
              ? 'Your basket changed while you were checking out'
              : caught.status === 402
                ? 'Your payment was declined'
                : 'We could not complete your order',
          detail: caught.problem.detail,
          items: caught.problem.errors?.map((error) => error.message),
        });

        // A 409 means stock or price moved. The basket on screen is stale, so
        // refetch it and send the customer back to the top of the flow rather
        // than letting them retry against a basket that no longer matches.
        if (caught.status === 409) {
          await refresh();
          setStep('details');
        }
      } else {
        setFailure({
          title: 'We could not complete your order',
          detail: 'The request failed before reaching the server. Nothing has been charged.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const steps: { id: Step; label: string }[] = [
    { id: 'details', label: 'Delivery' },
    { id: 'payment', label: 'Payment' },
    { id: 'review', label: 'Review' },
  ];
  const currentIndex = steps.findIndex((entry) => entry.id === step);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold tracking-tight">Checkout</h1>

      <ol className="mt-6 flex flex-wrap items-center gap-2 text-sm" aria-label="Checkout progress">
        {steps.map((entry, index) => {
          const done = index < currentIndex;
          const active = index === currentIndex;
          return (
            <li key={entry.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  // Only allow going back. Jumping forward would skip validation.
                  if (index < currentIndex) setStep(entry.id);
                }}
                disabled={index > currentIndex}
                aria-current={active ? 'step' : undefined}
                className={[
                  'flex items-center gap-2 rounded-full border px-3 py-1.5 transition-colors',
                  active
                    ? 'border-accent bg-accent-soft font-medium text-accent'
                    : done
                      ? 'border-line text-ink hover:bg-raised'
                      : 'border-line text-subtle',
                ].join(' ')}
              >
                <span className="tabular flex size-5 items-center justify-center rounded-full bg-surface text-xs">
                  {done ? '✓' : index + 1}
                </span>
                {entry.label}
              </button>
              {index < steps.length - 1 && <span className="text-subtle" aria-hidden="true">→</span>}
            </li>
          );
        })}
      </ol>

      {failure && (
        <div className="mt-6">
          <Alert tone="danger" title={failure.title} onDismiss={() => setFailure(null)}>
            <p>{failure.detail}</p>
            {failure.items && failure.items.length > 0 && (
              <ul className="mt-2 list-inside list-disc space-y-0.5">
                {failure.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </Alert>
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-6">
          {step === 'details' && (
            <Card className="p-6">
              <h2 className="text-base font-semibold">Delivery details</h2>
              <p className="mt-1 text-sm text-muted">
                {user
                  ? 'Signed in — this order will be attached to your account.'
                  : 'Checking out as a guest. Create an account afterwards to track this order.'}
              </p>

              <div className="mt-6 space-y-4">
                <Field
                  label="Email"
                  htmlFor="email"
                  error={fieldErrors['email']}
                  hint="Used for the order confirmation and nothing else."
                >
                  <TextInput
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    invalid={Boolean(fieldErrors['email'])}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full name" htmlFor="name" error={fieldErrors['shipping.name']}>
                    <TextInput
                      id="name"
                      autoComplete="name"
                      required
                      value={shipping.name}
                      invalid={Boolean(fieldErrors['shipping.name'])}
                      onChange={(event) => setShipping({ ...shipping, name: event.target.value })}
                    />
                  </Field>
                  <Field label="Phone (optional)" htmlFor="phone" error={fieldErrors['shipping.phone']}>
                    <TextInput
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      value={shipping.phone}
                      onChange={(event) => setShipping({ ...shipping, phone: event.target.value })}
                    />
                  </Field>
                </div>

                <Field label="Address" htmlFor="line1" error={fieldErrors['shipping.line1']}>
                  <TextInput
                    id="line1"
                    autoComplete="address-line1"
                    required
                    value={shipping.line1}
                    invalid={Boolean(fieldErrors['shipping.line1'])}
                    onChange={(event) => setShipping({ ...shipping, line1: event.target.value })}
                  />
                </Field>

                <Field label="Apartment, suite, etc. (optional)" htmlFor="line2">
                  <TextInput
                    id="line2"
                    autoComplete="address-line2"
                    value={shipping.line2}
                    onChange={(event) => setShipping({ ...shipping, line2: event.target.value })}
                  />
                </Field>

                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="City" htmlFor="city" error={fieldErrors['shipping.city']}>
                    <TextInput
                      id="city"
                      autoComplete="address-level2"
                      required
                      value={shipping.city}
                      invalid={Boolean(fieldErrors['shipping.city'])}
                      onChange={(event) => setShipping({ ...shipping, city: event.target.value })}
                    />
                  </Field>
                  <Field label="State / region" htmlFor="region">
                    <TextInput
                      id="region"
                      autoComplete="address-level1"
                      value={shipping.region}
                      onChange={(event) => setShipping({ ...shipping, region: event.target.value })}
                    />
                  </Field>
                  <Field label="Postal code" htmlFor="postalCode" error={fieldErrors['shipping.postalCode']}>
                    <TextInput
                      id="postalCode"
                      autoComplete="postal-code"
                      required
                      value={shipping.postalCode}
                      invalid={Boolean(fieldErrors['shipping.postalCode'])}
                      onChange={(event) => setShipping({ ...shipping, postalCode: event.target.value })}
                    />
                  </Field>
                </div>

                <Field label="Country" htmlFor="country" error={fieldErrors['shipping.country']}>
                  <Select
                    id="country"
                    value={shipping.country}
                    onChange={(event) => setShipping({ ...shipping, country: event.target.value })}
                  >
                    <option value="US">United States</option>
                    <option value="GB">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="IN">India</option>
                  </Select>
                </Field>

                <Field label="Delivery notes (optional)" htmlFor="notes">
                  <TextArea
                    id="notes"
                    rows={3}
                    maxLength={500}
                    value={notes}
                    placeholder="Leave with a neighbour, gate code, anything useful."
                    onChange={(event) => setNotes(event.target.value)}
                  />
                </Field>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  onClick={() => {
                    if (validateDetails()) setStep('payment');
                  }}
                >
                  Continue to payment
                </Button>
              </div>
            </Card>
          )}

          {step === 'payment' && (
            <Card className="p-6">
              <h2 className="text-base font-semibold">Payment</h2>
              <div className="mt-3">
                <Alert tone="warning" title="This is a demonstration checkout">
                  No card details are collected and no money moves. The provider below reproduces
                  Stripe's test-mode outcomes, including declines, so the failure path is real code.
                </Alert>
              </div>

              <fieldset className="mt-6 space-y-2">
                <legend className="text-sm font-medium">Test card</legend>
                {TEST_CARDS.map((card) => (
                  <label
                    key={card.token}
                    className={[
                      'flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors',
                      paymentToken === card.token
                        ? 'border-accent bg-accent-soft'
                        : 'border-line hover:bg-raised',
                    ].join(' ')}
                  >
                    <input
                      type="radio"
                      name="paymentToken"
                      value={card.token}
                      checked={paymentToken === card.token}
                      onChange={() => setPaymentToken(card.token)}
                      className="size-4 accent-[var(--accent)]"
                    />
                    <span className="flex-1">{card.label}</span>
                    <code className="hidden text-xs text-subtle sm:block">{card.token}</code>
                  </label>
                ))}
              </fieldset>

              <div className="mt-6 flex justify-between">
                <Button variant="ghost" onClick={() => setStep('details')}>
                  Back
                </Button>
                <Button onClick={() => setStep('review')}>Review order</Button>
              </div>
            </Card>
          )}

          {step === 'review' && (
            <Card className="p-6">
              <h2 className="text-base font-semibold">Review</h2>

              <dl className="mt-5 space-y-4 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-subtle">Delivering to</dt>
                  <dd className="mt-1">
                    <p className="font-medium">{shipping.name}</p>
                    <p className="text-muted">{shipping.line1}</p>
                    {shipping.line2 && <p className="text-muted">{shipping.line2}</p>}
                    <p className="text-muted">
                      {shipping.city}
                      {shipping.region ? `, ${shipping.region}` : ''} {shipping.postalCode}
                    </p>
                    <p className="text-muted">{shipping.country}</p>
                  </dd>
                </div>

                <div>
                  <dt className="text-xs uppercase tracking-wide text-subtle">Contact</dt>
                  <dd className="mt-1 text-muted">{email}</dd>
                </div>

                <div>
                  <dt className="text-xs uppercase tracking-wide text-subtle">Paying with</dt>
                  <dd className="mt-1 text-muted">
                    {TEST_CARDS.find((card) => card.token === paymentToken)?.label ?? paymentToken}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex justify-between">
                <Button variant="ghost" onClick={() => setStep('payment')}>
                  Back
                </Button>
                <Button size="lg" loading={submitting} onClick={() => void submit()}>
                  Place order — {money(cart.totals.totalMinor, cart.currency)}
                </Button>
              </div>
            </Card>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <Card className="p-5">
            <h2 className="text-sm font-semibold">Order summary</h2>

            <ul className="mt-4 space-y-3">
              {cart.items.map((item) => (
                <li key={item.id} className="flex gap-3">
                  <div className="relative size-14 shrink-0 overflow-hidden rounded-lg border border-line bg-sunken">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt="" className="size-full object-cover" />
                    ) : null}
                    <span className="tabular absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-ink text-[10px] font-semibold text-canvas">
                      {item.quantity}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <p className="tabular text-xs text-subtle">
                      {money(item.unitPriceMinor, cart.currency)} each
                    </p>
                  </div>
                  <span className="tabular text-sm">{money(item.lineTotalMinor, cart.currency)}</span>
                </li>
              ))}
            </ul>

            <dl className="mt-5 space-y-1.5 border-t border-line pt-4 text-sm">
              <SummaryRow label="Subtotal" value={money(totals.subtotalMinor, cart.currency)} />
              <SummaryRow
                label="Shipping"
                value={
                  cart.totals.shippingMinor === 0 ? 'Free' : money(cart.totals.shippingMinor, cart.currency)
                }
              />
              <SummaryRow label="Tax (8%)" value={money(cart.totals.taxMinor, cart.currency)} />
              <div className="flex items-center justify-between border-t border-line pt-2 text-base font-semibold">
                <dt>Total</dt>
                <dd className="tabular">{money(cart.totals.totalMinor, cart.currency)}</dd>
              </div>
            </dl>

            <Badge tone="neutral" className="mt-4 w-full justify-center py-1">
              Stock is reserved only when the order is placed
            </Badge>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}
